import { type TablePaginationConfig } from "antd"
import { type ColumnsType } from "antd/es/table"
import type { SorterResult } from "antd/es/table/interface"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../../../components/EntityTable/surfaces.ts"
import { TableResultCount } from "../../../../../../../components/Table/TableResultCount.tsx"
import { useScrolledTable } from "../../../../../../../hooks/use-scrolled-table.ts"
import { cn } from "../../../../../../../utils/cn.ts"
import { getTableOrderingFromSorter } from "../../../../../../../utils/table.ts"
import type { ProjectSubcontractor } from "../../../../../types/project-subcontractors.types.tsx"
import { getColumns } from "./columns.tsx"

interface ProjectViewSubcontractorsListProps {
  selectedId?: string | null
  subcontractors: ProjectSubcontractor[]
  fetching: boolean
  next?: string | null
  /** The page a load-more failed on, offered back. */
  retryNext?: string | null
  count: number
  configControl: { open: boolean; onOpenChange: (open: boolean) => void }
  onOrderingChange: (ordering: string | null) => void
  onLoadMore: () => void
  onItemSelected: (item: ProjectSubcontractor) => void
}

export function ProjectViewSubcontractorsList({
  subcontractors,
  selectedId,
  fetching,
  next,
  retryNext,
  count,
  configControl,
  onOrderingChange,
  onLoadMore,
  onItemSelected
}: ProjectViewSubcontractorsListProps) {
  const { t } = useTranslation()

  const columns: ColumnsType<ProjectSubcontractor> = useMemo(
    () => getColumns({ t, onItemSelected }),
    [t, onItemSelected]
  )

  // The same party can appear on several rows (e.g. Payable + Expense), so `id`
  // is not unique per row. Keying the table by `id` gives React duplicate keys,
  // and after a server-side sort the rows never re-order in the DOM. Build a
  // stable, unique key per row instead.
  const rows = useMemo(
    () =>
      (subcontractors || []).map((row, index) => ({
        ...row,
        rowKey: `${row.id}-${row.financial_type}-${row.is_expense}-${index}`
      })),
    [subcontractors]
  )

  const onChangeTable = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, unknown>,
      sorter:
        | SorterResult<ProjectSubcontractor>
        | SorterResult<ProjectSubcontractor>[]
    ) => {
      onOrderingChange(getTableOrderingFromSorter(sorter))
    },
    [onOrderingChange]
  )

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: fetching,
    next: next,
    loadMore: onLoadMore
  })

  return (
    <div ref={tableRef}>
      <EntityTable<ProjectSubcontractor>
        surface={TABLE_SURFACES.financialSummary}
        selectionMode="on-demand"
        tab={DEFAULT_TAB}
        withOutPagination
        hideConfigButton
        configControl={configControl}
        rowKey="rowKey"
        size="small"
        rowHoverable={false}
        onScroll={onTableScroll}
        rowClassName={(record) =>
          record?.id && record?.id === selectedId
            ? "bg-primary/20 font-semibold"
            : ""
        }
        className={cn(
          "[&_th]:bg-primary [&_th]:rounded-none [&_th]:text-white",
          {
            "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
              !subcontractors?.length
          }
        )}
        columns={columns}
        onChange={onChangeTable}
        scroll={{
          x: 1300,
          y: tableHeight,
          scrollToFirstRowOnChange: true
        }}
        pagination={false}
        dataSource={rows}
        loading={fetching && !subcontractors?.length}
      />
      <TableResultCount
        loaded={subcontractors?.length ?? 0}
        total={count}
        loading={fetching}
        next={next}
        retryNext={retryNext}
        onRetry={onLoadMore}
      />
    </div>
  )
}
